import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';

import dayjs from 'dayjs';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import { SectionCard, EmptyState } from './ui';
import { TRUE_COLORS } from '../theme';

const ACTIONS: Record<number, { verb: string; icon: React.ReactNode; color: string; bg: string }> = {
  1: { verb: 'agregó', icon: <AddIcon fontSize="small" />, color: TRUE_COLORS.success, bg: 'rgba(46,125,91,0.12)' },
  2: { verb: 'editó', icon: <EditOutlinedIcon fontSize="small" />, color: TRUE_COLORS.primary, bg: 'rgba(111,137,148,0.16)' },
  3: { verb: 'eliminó', icon: <DeleteOutlineIcon fontSize="small" />, color: TRUE_COLORS.danger, bg: 'rgba(192,57,43,0.1)' },
};

/** Where an entry's object lives in this app, when it has a page. */
const linkFor = (entry: any) => {
  if (entry.action_flag === 3 || !entry.object_id) return null;
  const type = (entry.content_type?.name ?? '').toLowerCase();
  if (type.startsWith('producto')) return `/admin/products/${entry.object_id}`;
  if (type.startsWith('cliente')) return `/admin/clients/${entry.object_id}`;
  if (type.startsWith('sucursal') || type === 'location') return `/admin/locations/${entry.object_id}`;
  return null;
};

const dayLabel = (d: dayjs.Dayjs) => {
  if (d.isSame(dayjs(), 'day')) return 'Hoy';
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Ayer';
  const label = d.format('dddd D [de] MMMM');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const PAGE = 60;

const Activity = () => {
  const axios = useAxios();
  const [activity, setActivity] = React.useState<any[]>([]);
  const [members, setMembers] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [action, setAction] = React.useState<'all' | '1' | '2' | '3'>('all');
  const [person, setPerson] = React.useState('all');
  const [shown, setShown] = React.useState(PAGE);

  React.useEffect(() => {
    (async () => {
      const [a, m] = await Promise.allSettled([axios.get('api/activity/'), axios.get('management/members/')]);
      if (a.status === 'fulfilled') setActivity([...(a.value.data ?? [])].sort((x, y) => dayjs(y.action_time).diff(dayjs(x.action_time))));
      if (m.status === 'fulfilled') setMembers(m.value.data ?? []);
      setLoading(false);
    })();
  }, [axios]);

  React.useEffect(() => { setShown(PAGE); }, [action, person]);

  const nameFor = (user: any) => {
    const m = members.find(x => x.member?.id === user?.id);
    return [m?.name, m?.lastname].filter(Boolean).join(' ') || m?.login || user?.username || 'Alguien';
  };

  const filtered = activity.filter(e => (action === 'all' || String(e.action_flag) === action) && (person === 'all' || String(e.user?.id) === person));
  const visible = filtered.slice(0, shown);
  const groups = visible.reduce<{ label: string; items: any[] }[]>((acc, e) => {
    const label = dayLabel(dayjs(e.action_time));
    const last = acc[acc.length - 1];
    if (last?.label === label) last.items.push(e); else acc.push({ label, items: [e] });
    return acc;
  }, []);

  return (
    <PageShell
      title="Actividad"
      subtitle="Todo lo que tu equipo agrega, edita o elimina"
      toolbar={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: { sm: 'center' } }}>
          <ToggleButtonGroup size="small" exclusive value={action} onChange={(_, v) => v && setAction(v)}
            sx={{ bgcolor: 'background.paper', '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 1.75 } }}>
            <ToggleButton value="all">Todo</ToggleButton>
            <ToggleButton value="1">Altas</ToggleButton>
            <ToggleButton value="2">Cambios</ToggleButton>
            <ToggleButton value="3">Bajas</ToggleButton>
          </ToggleButtonGroup>
          <TextField select size="small" label="Persona" value={person} onChange={e => setPerson(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="all">Todo el equipo</MenuItem>
            {members.map(m => <MenuItem key={m.id} value={String(m.member?.id)}>{nameFor(m.member)}</MenuItem>)}
          </TextField>
        </Stack>
      }
    >
      <Box sx={{ maxWidth: 820 }}>
        {loading ? (
          <SectionCard><Stack spacing={2}>{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={48} />)}</Stack></SectionCard>
        ) : filtered.length === 0 ? (
          <SectionCard><EmptyState icon={<HistoryIcon />} title="Sin actividad" description="Los cambios en productos, clientes, sucursales y más aparecerán aquí." /></SectionCard>
        ) : (
          <Stack spacing={3}>
            {groups.map(group => (
              <Box key={group.label}>
                <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>{group.label}</Typography>
                <SectionCard noPadding>
                  {group.items.map((e, i) => {
                    const a = ACTIONS[e.action_flag] ?? ACTIONS[2];
                    const to = linkFor(e);
                    return (
                      <Stack
                        key={e.id}
                        direction="row"
                        spacing={2}
                        sx={{ px: 2.5, py: 1.75, alignItems: 'center', borderTop: i ? `1px solid ${TRUE_COLORS.borderSolid}` : 'none' }}
                      >
                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: a.bg, color: a.color, flexShrink: 0 }}>
                          {a.icon}
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.875rem', color: 'text.primary' }}>
                            <strong>{nameFor(e.user)}</strong> {a.verb} {(e.content_type?.name ?? 'un registro').toLowerCase()}{' '}
                            {to
                              ? <Link component={RouterLink} to={to} sx={{ fontWeight: 600, color: 'text.primary' }}>«{e.object_repr}»</Link>
                              : <strong>«{e.object_repr}»</strong>}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', flexShrink: 0, fontSize: '0.75rem' }}>
                          {dayjs(e.action_time).format('HH:mm')}
                        </Typography>
                      </Stack>
                    );
                  })}
                </SectionCard>
              </Box>
            ))}
            {filtered.length > shown && (
              <Button onClick={() => setShown(s => s + PAGE)} sx={{ alignSelf: 'center' }}>Ver más</Button>
            )}
          </Stack>
        )}
      </Box>
    </PageShell>
  );
};

export default Activity;
